import { ApexOptions } from 'apexcharts';
import dayjs from 'dayjs';

import store from '@/store';

import { IChartResult } from '@/models/performance';
import { IPeriod } from '@/models/period';
import { EInitiativesDictionary } from '@/models/initiatives';

import { ValidatorState } from '@/router/validators/types';
import {
  birthdayClubValidator,
  customerImportValidator,
  discountsAndCuponsValidator,
  freeCoinsValidator,
  loyaltyWidgetValidator,
  offersValidator,
  sellingCoinsValidator,
  socialRewardsValidator
} from '@/router/validators/navbar';

import DateUtils, { USER_TIMEZONE } from './DateUtils';
import { treatValue } from './moneyTreatments';
import { abbreviateNumber } from './currency';

export enum EChartTooltipCounterFormatNumber {
  TREAT_VALUE = 'treatValue',
  ABBREVIATE_NUMBER = 'abbreviateNumber'
}

type ChartOptionsParams = {
  colors?: string[];
  prefixTooltipLegend?: string;
  tooltipCounterFormatNumber?: EChartTooltipCounterFormatNumber;
};

export enum EPeriodPerformance {
  WEEK = 'this_week',
  MONTH = 'this_month',
  OVERTIME = 'over_time',
  TODAY = 'today'
}

export type DatePickerShortcut = keyof typeof PerformanceUtils.datepickerShortcutMapper;

export default class PerformanceUtils {
  static get datepickerShortcutMapper() {
    return {
      isoWeek: 'this_week',
      '-isoWeek': 'last_week',
      7: 'last_7_days',
      30: 'last_30_days',
      month: 'this_month',
      '-month': 'last_month',
      year: 'this_year',
      '-year': 'last_year'
    };
  }

  static getComparativeLabelV2(shortcut: EPeriodPerformance) {
    const labelMapper: Record<string, string> = {
      [EPeriodPerformance.WEEK]: 'from last week',
      [EPeriodPerformance.MONTH]: 'from last month',
      [EPeriodPerformance.TODAY]: 'from yesterday'
    };

    return labelMapper[shortcut] || '';
  }

  static getComparativeLabel(shortcut: DatePickerShortcut) {
    const labelMapper: Record<DatePickerShortcut, string> = {
      isoWeek: 'from last week',
      '-isoWeek': 'from previous week',
      7: 'from previous 7 days',
      30: 'from previous 30 days',
      month: 'from last month',
      '-month': 'from previous month',
      year: 'from last year',
      '-year': 'from previous year'
    };

    return labelMapper[shortcut];
  }

  static getFiltersFiltered(filters: { type: string; label: string }[]) {
    return filters.filter(item => {
      const validationMapper = this.validationsBySlug();

      if (validationMapper.hasOwnProperty(item.type)) {
        return validationMapper[item.type].isValid;
      }

      return true;
    });
  }

  static formatCounterLabelLegend(
    counter: number,
    format: string
  ): number | string {
    const formatsAvailables: Record<string, string> = {
      [EChartTooltipCounterFormatNumber.TREAT_VALUE]:
        treatValue(counter) || '0',
      [EChartTooltipCounterFormatNumber.ABBREVIATE_NUMBER]:
        abbreviateNumber(counter) || '0'
    };

    return formatsAvailables[format] || counter;
  }

  static chartOptions({
    colors,
    prefixTooltipLegend,
    tooltipCounterFormatNumber
  }: ChartOptionsParams = {}): ApexOptions {
    return {
      chart: {
        fontFamily: 'Inter, Roboto, sans-serif',
        foreColor: '#92A2B0',
        toolbar: {
          show: false
        },
        zoom: {
          enabled: false
        },
        sparkline: {
          enabled: false
        }
      },
      xaxis: {
        type: 'datetime',
        labels: {
          style: {
            cssClass: 'apexcharts-xaxis-label'
          },
          datetimeFormatter: {
            month: 'MMM',
            day: 'MMM dd'
          }
        },
        tooltip: {
          enabled: false
        }
      },
      grid: {
        borderColor: '#F0F3F5',
        padding: {
          right: 24
        }
      },
      yaxis: {
        min: 0,
        tickAmount: 3,
        labels: {
          style: {
            cssClass: 'apexcharts-yaxis-label'
          },
          formatter: value => String(value ? value.toFixed(0) : '')
        }
      },
      legend: {
        show: false
      },
      stroke: {
        width: 2
      },
      colors,
      tooltip: {
        custom({ series, seriesIndex, dataPointIndex, w }) {
          const allSeries = w.globals.initialSeries;
          const colors = w.globals.colors;

          const labels = series
            .map((serie: number[], index: number) => {
              const counter = tooltipCounterFormatNumber
                ? PerformanceUtils.formatCounterLabelLegend(
                    serie[dataPointIndex],
                    tooltipCounterFormatNumber
                  )
                : serie[dataPointIndex];

              const labelFormatted = prefixTooltipLegend
                ? `${prefixTooltipLegend} ${counter} ${allSeries[index].name}`
                : `${counter} ${allSeries[index].name}`;

              return {
                label: labelFormatted,
                color: colors[index],
                show: counter !== undefined
              };
            })
            .filter((item: any) => item.show);

          const [date] = allSeries[seriesIndex].data[dataPointIndex];

          return `
            <div class="apexcharts-custom-tooltip">
              <span class="apexcharts-custom-tooltip__header">
                ${DateUtils.formattedDate({
                  date,
                  applyTimezone: true
                })}
              </span>
              <ul class="apexcharts-custom-tooltip__list">
                ${labels
                  .map(
                    (item: { label: string; color: string }) => `<li>
                      <i style="background-color: ${item.color};"></i>
                      <span>${item.label}</span>
                    </li>`
                  )
                  .join('\n')}
              </ul>
            </div>
          `;
        }
      }
    };
  }

  static getPeriodBySlug(periodSlug: EPeriodPerformance) {
    const mapperStartDate: Record<EPeriodPerformance, any> = {
      [EPeriodPerformance.MONTH]: 'month',
      [EPeriodPerformance.WEEK]: 'week',
      [EPeriodPerformance.TODAY]: 'day',
      [EPeriodPerformance.OVERTIME]: 'year'
    };

    return {
      start: dayjs()
        .tz(USER_TIMEZONE)
        .startOf(mapperStartDate[periodSlug])
        .toISOString(),
      end: dayjs()
        .endOf(mapperStartDate[periodSlug])
        .toISOString(),
      shortcut: periodSlug
    };
  }

  static defaultPeriod(startOf: dayjs.OpUnitType = 'year') {
    const formatedPeriod = {
      start: dayjs()
        .tz(USER_TIMEZONE)
        .startOf(startOf)
        .toISOString(),
      end: dayjs()
        .endOf('day')
        .toISOString()
    };

    return this.normalizedDateValues(formatedPeriod, false);
  }

  static periodOfThirtyDays() {
    const formatedPeriod = {
      start: dayjs()
        .subtract(30, 'days')
        .startOf('day')
        .toISOString(),
      end: dayjs()
        .subtract(1, 'days')
        .endOf('day')
        .toISOString(),
      shortcut: 30
    };

    return this.normalizedDateValues(formatedPeriod);
  }

  static defaultPeriodForFilter() {
    const { start, end } = this.defaultPeriod();

    return {
      from: start,
      to: end
    };
  }

  static normalizedDateValues(
    filters: Record<string, any> = {},
    shouldRemoveTime = false
  ) {
    const dateKeys = new Set(['start', 'end', 'from', 'to']);

    const entries = Object.entries(filters).map(([filterKey, filterValue]) => {
      if (dateKeys.has(filterKey) && filterValue.length && shouldRemoveTime) {
        const [dateValue] = filterValue.split('T');

        return [filterKey, dateValue];
      }

      return [filterKey, filterValue];
    });

    return Object.fromEntries(entries);
  }

  static normalizedChartSeries(
    chartResult: IChartResult,
    showAllCharts = false,
    removeAfterToday = false
  ) {
    const { datasets, labels } = chartResult;

    const shouldShowChart = datasets
      .map(({ data }) => data.some(Boolean))
      .some(Boolean);

    if (!shouldShowChart && !showAllCharts) {
      return [];
    }

    return datasets.map(({ label, data, additionalData }) => ({
      data: data
        .map((value, index) => {
          const [dateWithoutTime] = labels[index].split('T');

          const dateLabel = dayjs(dateWithoutTime)
            .utc()
            .format();

          return [
            dateLabel,
            value,
            additionalData ? additionalData[index] : null
          ];
        })
        .filter(([label, data]) =>
          removeAfterToday
            ? dayjs(label).isBefore(
                dayjs()
                  .endOf('day')
                  .toISOString()
              )
            : true
        ),
      name: label
    }));
  }

  static normalizedPeriod(period: IPeriod) {
    const removeCurrentDayFromShortcuts = new Set([
      'last_7_days',
      'last_30_days'
    ]);

    const translatedShortcut = this.datepickerShortcutMapper[
      period.shortcut as DatePickerShortcut
    ];

    const currDate = dayjs().format();

    if (dayjs(period.end).isAfter(currDate)) {
      period.end = dayjs()
        .endOf('day')
        .toISOString();
    }

    if (removeCurrentDayFromShortcuts.has(translatedShortcut)) {
      period.end = dayjs(period.end)
        .subtract(1, 'day')
        .toISOString();
    }

    const utcOffset = new Date().getTimezoneOffset();

    if (translatedShortcut === this.datepickerShortcutMapper.year) {
      period.start = dayjs()
        .tz(USER_TIMEZONE)
        .startOf('year')
        .toISOString();
    }

    if (translatedShortcut === this.datepickerShortcutMapper['-year']) {
      period.start = dayjs()
        .tz(USER_TIMEZONE)
        .subtract(1, 'year')
        .startOf('year')
        .toISOString();
    }

    const transformedPeriod = {
      ...period,
      ...{
        start: dayjs(period.start)
          .utcOffset(utcOffset)
          .toISOString(),
        end: dayjs(period.end)
          .utcOffset(utcOffset)
          .toISOString()
      }
    };

    return this.normalizedDateValues(transformedPeriod);
  }

  static validationsBySlug(): Record<string, ValidatorState> {
    return {
      [EInitiativesDictionary.FREE_COINS]: freeCoinsValidator(),
      [EInitiativesDictionary.REFERRAL_PROGRAM]: loyaltyWidgetValidator(),
      [EInitiativesDictionary.DEALS_AND_DISCOUNTS]: discountsAndCuponsValidator(),
      [EInitiativesDictionary.BIRTHDAY_CLUB]: birthdayClubValidator(),
      [EInitiativesDictionary.GIFT_COINS]: sellingCoinsValidator(),
      [EInitiativesDictionary.INVITATION_CAMPAIGN]: customerImportValidator(),
      [EInitiativesDictionary.SPECIAL_COINS]: offersValidator(),
      [EInitiativesDictionary.SOCIAL_REWARDS]: socialRewardsValidator(),
      [EInitiativesDictionary.CUSTOMER_ACQUISITION]: customerImportValidator(),
      [EInitiativesDictionary.TRANSPARENT_SPECIAL_OFFER_BIRTHDAY]: offersValidator(),
      [EInitiativesDictionary.TRANSPARENT_SPECIAL_OFFER_PRODUCT_SERVICE]: offersValidator(),
      [EInitiativesDictionary.TRANSPARENT_SPECIAL_OFFER_STOREWIDE]: offersValidator()
    };
  }

  static filteredDataBySlug<T = any>(dataArray: Array<T>, slugKey: string) {
    const validationMapper = this.validationsBySlug();

    return dataArray.filter((data: any) => {
      const slug = data[slugKey] as string;

      if (validationMapper.hasOwnProperty(slug)) {
        return validationMapper[slug].isValid;
      }

      return true;
    });
  }
}
